import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { InvitationDetails } from '../types';

export function useInviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    logout,
    setAuthSession,
    switchOrganization,
    refreshOrganizations,
  } = useAuth();

  const [invite, setInvite] = useState<InvitationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadError('Missing invitation token in link.');
      setIsLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const data = await apiClient<InvitationDetails>(`/invitations/${token}`, {
          headers: { Authorization: '' },
        });
        setInvite(data);
      } catch (err: any) {
        if (err.status === 410) {
          setLoadError(err.message || 'This invitation has already been accepted, revoked, or has expired.');
        } else if (err.status === 404) {
          setLoadError('Invitation link is invalid or could not be found.');
        } else {
          setLoadError(err.message || 'Failed to load invitation. Please contact your administrator.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Handle Authenticated 1-Click Accept
  const handleAuthenticatedAccept = async () => {
    if (!token || !invite) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const res = await apiClient<{
        message: string;
        user?: any;
        organization?: any;
        project?: any;
        role?: string;
        permission_tier?: string;
      }>(`/invitations/${token}/accept`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (res.organization) {
        await switchOrganization(res.organization);
      }
      await refreshOrganizations();
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Unauthenticated Accept (Sign In or Register)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const isExistingUser = Boolean(invite?.user_exists);

    if (!isExistingUser && !fullName.trim()) {
      setSubmitError('Please enter your full name.');
      return;
    }
    if (!password) {
      setSubmitError('Please enter your password.');
      return;
    }
    if (!isExistingUser && password.length < 8) {
      setSubmitError('Password must be at least 8 characters long.');
      return;
    }
    if (!isExistingUser && password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await apiClient<{
        token: string;
        user: any;
        organization: any;
        project: any;
        role?: string;
        permission_tier?: any;
      }>(`/invitations/${token}/accept`, {
        method: 'POST',
        headers: { Authorization: '' },
        body: JSON.stringify({
          full_name: isExistingUser ? undefined : fullName.trim(),
          password,
        }),
      });

      setAuthSession(res.token, res.user, res.organization, res.project, res.role, res.permission_tier);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to accept invitation. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMatchingEmail = Boolean(
    user && invite && user.email.toLowerCase() === invite.email.toLowerCase()
  );

  return {
    token,
    user,
    isAuthenticated,
    isMatchingEmail,
    invite,
    isLoading,
    loadError,
    fullName,
    setFullName,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    isSubmitting,
    submitError,
    handleAuthenticatedAccept,
    handleSubmit,
    logout,
  };
}
